import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Permission, UserRole } from '../../server/src/models/User';
import { UserService } from '../services/user.service';
import { User } from '../types/user.types';

interface CreateUserData {
  name: string;
  email: string;
  password: string;
  role: string;
}

export interface NewUserForm {
  name: string;
  email: string;
  password: string;
  isAdmin: boolean;
}

export function useUserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [opened, setOpened] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [createModalOpened, setCreateModalOpened] = useState(false);
  
  const navigate = useNavigate();

  // Form for creating a new user
  const createUserForm = useForm<NewUserForm>({
    initialValues: {
      name: '',
      email: '',
      password: '',
      isAdmin: false
    },
    validate: {
      name: (value) => (value.length < 2 ? 'Name must be at least 2 characters' : null),
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
      password: (value) => (value.length < 6 ? 'Password must be at least 6 characters' : null)
    }
  });

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await UserService.getUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Auto-hide error and success messages after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => clearError(), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => clearSuccess(), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setOpened(true);
  };

  const handleCloseModal = () => {
    setOpened(false);
    setSelectedUser(null);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    try {
      setIsLoading(true);
      const permissions = selectedUser.userPermissions.map(p => p.permission);
      await UserService.updatePermissions(selectedUser._id, permissions);
      
      setSuccess('User permissions updated successfully');
      fetchUsers();
      handleCloseModal();
    } catch (err) {
      setError('Failed to update user permissions');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePermissionChange = (permission: Permission) => {
    if (!selectedUser) return;

    const newPermissions = selectedUser.userPermissions.some(p => p.permission === permission)
      ? selectedUser.userPermissions.filter(p => p.permission !== permission)
      : [...selectedUser.userPermissions, { permission }];

    setSelectedUser({
      ...selectedUser,
      userPermissions: newPermissions
    });
  };

  const createUser = async (userData: CreateUserData) => {
    try {
      setIsLoading(true);
      await UserService.createUser(userData);
      setSuccess('User created successfully');
      fetchUsers();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCreateUser = async (values: NewUserForm) => {
    const success = await createUser({
      name: values.name,
      email: values.email,
      password: values.password,
      role: values.isAdmin ? UserRole.ADMIN : UserRole.USER
    });
    
    if (success) {
      setCreateModalOpened(false);
      createUserForm.reset();
    }
  };
  
  const navigateBack = () => {
    navigate('/');
  };
  
  const clearError = () => setError('');
  const clearSuccess = () => setSuccess('');

  return {
    // State
    users,
    opened,
    selectedUser,
    error,
    success,
    isLoading,
    createModalOpened,
    createUserForm,
    
    // Actions
    handleEditUser,
    handleCloseModal,
    handleSaveUser,
    handlePermissionChange,
    createUser,
    handleCreateUser,
    navigateBack,
    setCreateModalOpened,
    clearError,
    clearSuccess
  };
} 